import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { settlementInventories, type SettlementInventory } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'documents.settlement_inventories.create';

const STATE_LABEL: Record<string, string> = {
  IN_PROGRESS: 'У процесі', COMPLETED: 'Завершено',
};

export default function SettlementInventoriesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)), [perms.availableOrgs],
  );
  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['settlement-inventories', orgId],
    queryFn: () => settlementInventories.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<SettlementInventory>[]>(
    () => [
      { key: 'document_number', header: 'Номер', width: 140, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 110, sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '' },
      { key: 'inventory_date', header: 'Дата інвент.', width: 130, sortValue: (r) => r.inventory_date ?? '', cell: (r) => r.inventory_date },
      { key: 'total_book_amount', header: 'Книжкова сума', width: 150, align: 'right', sortValue: (r) => Number(r.total_book_amount), cell: (r) => r.total_book_amount },
      { key: 'total_confirmed_amount', header: 'Підтверджено', width: 150, align: 'right', sortValue: (r) => Number(r.total_confirmed_amount), cell: (r) => r.total_confirmed_amount },
      { key: 'total_variance_amount', header: 'Розбіжність', width: 150, align: 'right', sortValue: (r) => Number(r.total_variance_amount), cell: (r) => r.total_variance_amount },
      { key: 'state', header: 'Стан', width: 130, sortValue: (r) => r.state ?? '',
        cell: (r) => STATE_LABEL[r.state] ?? r.state },
    ], [],
  );
  const canCreate = !!orgId && allowedOrgIds.has(orgId);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Інвентаризація розрахунків</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <button className="primary" disabled={!canCreate} style={{ marginLeft: 'auto' }}
          onClick={() => navigate(`/settlements/settlement-inventories/new?org=${orgId}`)}>
          + Інвентаризація
        </button>
      </div>
      <DataTable<SettlementInventory>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/settlements/settlement-inventories/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
